/**
 * Retry Policy Utility for Frontend
 * Provides configurable retry mechanism with exponential backoff for API calls
 */

export interface RetryOptions {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  multiplier: number;
  jitter: number;
}

export class RetryPolicy {
  private maxRetries: number;
  private baseDelay: number;
  private maxDelay: number;
  private multiplier: number;
  private jitter: number;

  constructor(options: RetryOptions) {
    this.maxRetries = options.maxRetries;
    this.baseDelay = options.baseDelay;
    this.maxDelay = options.maxDelay;
    this.multiplier = options.multiplier;
    this.jitter = options.jitter;
  }

  /**
   * Check if an error should be retried
   */
  private shouldRetry(error: any): boolean {
    if (!error) return false;

    // Network errors that should be retried
    const networkErrors = [
      'ECONNRESET',
      'ETIMEDOUT', 
      'ENOTFOUND',
      'ECONNREFUSED',
      'Network Error',
      'Request timeout',
      'socket hang up',
      'ECONNABORTED'
    ];

    // HTTP status codes that should be retried
    const retryableStatusCodes = [408, 429, 502, 503, 504];

    // Check for network errors
    if (networkErrors.some(netError => 
      error.message?.toLowerCase().includes(netError.toLowerCase()) ||
      error.code === netError
    )) {
      return true;
    }

    // Check for HTTP status codes
    if (error.status && retryableStatusCodes.includes(error.status)) {
      return true;
    }

    // Check for axios specific errors
    if (error.response && retryableStatusCodes.includes(error.response.status)) {
      return true;
    }

    // Check axios connection errors
    if (error.request && !error.response) {
      return true;
    }

    return false;
  }

  /**
   * Calculate delay with exponential backoff and jitter
   */
  private calculateDelay(attempt: number): number {
    const exponentialDelay = this.baseDelay * Math.pow(this.multiplier, attempt);
    const delay = Math.min(exponentialDelay, this.maxDelay);
    
    // Add jitter to prevent thundering herd
    const jitterAmount = delay * this.jitter * (Math.random() * 2 - 1);
    return Math.max(100, delay + jitterAmount); // Minimum 100ms delay
  }

  /**
   * Sleep utility for delays
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Execute a function with retry logic
   */
  async executeWithRetry<T>(
    fn: () => Promise<T>, 
    operationName: string = 'API call', 
    context: any = {}
  ): Promise<T> {
    let lastError: any = null;

    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      try {
        const result = await fn();
        
        // Log success if retries were made
        if (attempt > 0) {
          console.log(`✅ ${operationName} succeeded after ${attempt} retries`, {
            ...context,
            successAttempt: attempt + 1
          });
        }

        return result;
      } catch (error: any) {
        lastError = error;

        // Log the error attempt
        console.warn(`⚠️ ${operationName} attempt ${attempt + 1}/${this.maxRetries + 1} failed:`, {
          error: error.message,
          status: error.status || error.response?.status,
          ...context,
          attempt: attempt + 1
        });

        // Check if we should retry
        if (attempt < this.maxRetries && this.shouldRetry(error)) {
          const delay = this.calculateDelay(attempt);
          console.log(`🔄 Retrying ${operationName} in ${delay}ms (attempt ${attempt + 2}/${this.maxRetries + 1})`);
          
          await this.sleep(delay);
          continue;
        } else {
          // Not retryable or max retries exceeded
          break;
        }
      }
    }

    // All retries exhausted
    console.error(`💥 ${operationName} failed after ${this.maxRetries + 1} attempts:`, {
      error: lastError?.message,
      finalError: lastError,
      ...context,
      totalAttempts: this.maxRetries + 1
    });

    throw lastError;
  }

  /**
   * Create a wrapper function with retry logic for API calls
   */
  wrapApiCall<T>(
    apiCall: () => Promise<T>, 
    operationName: string, 
    context: any = {}
  ): () => Promise<T> {
    return () => this.executeWithRetry(apiCall, operationName, context);
  }
}

// Default retry policy instance
export const defaultRetryPolicy = new RetryPolicy({
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 20000,
  multiplier: 2,
  jitter: 0.1
});

// Preset configurations for different scenarios
export const retryPolicies = {
  // Standard API calls
  standard: defaultRetryPolicy,
  
  // Quick retry for less critical operations
  quick: new RetryPolicy({
    maxRetries: 2,
    baseDelay: 500,
    maxDelay: 3000,
    multiplier: 2,
    jitter: 0.1
  }),
  
  // Aggressive retry for critical operations
  aggressive: new RetryPolicy({
    maxRetries: 5,
    baseDelay: 1500,
    maxDelay: 15000,
    multiplier: 2,
    jitter: 0.15
  }),
  
  // Conservative retry for non-critical operations
  conservative: new RetryPolicy({
    maxRetries: 1,
    baseDelay: 2000,
    maxDelay: 5000,
    multiplier: 2,
    jitter: 0.05
  })
};
